import {
  SITE_NAME,
  PROJECTS,
  HOME_CHANNEL_ID,
  indexOfId,
  projectAt,
  parseChannelFromPath,
  loadNativeChannel,
} from '../channels/registry.js';
import { createLegacyIframeChannel } from './legacyIframe.js';
import { bus, assertBusHealthy, fadeOutMaster, fadeInMaster } from '../shared/audioBus.js';
import { createChannelInfo } from './channelInfo.js';
import './channelInfo.css';

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

export async function createShell(root) {
  await loadScript('/vcr-menu.js');

  root.innerHTML = `
    <div id="nt-shell" class="nt-shell">
      <header class="vcr-ch-header" id="nt-shell-header">
        <div class="vcr-ch-header__chan">
          <button type="button" class="vcr-ch-header__step" data-dir="-1" aria-label="Previous channel">&lt;</button>
          <button type="button" class="vcr-ch-header__step" data-dir="1" aria-label="Next channel">&gt;</button>
          <button type="button" class="vcr-ch-header__title" id="nt-shell-title" aria-haspopup="dialog" aria-expanded="false" aria-label="About this channel">NOISE TABLE</button>
        </div>
        <button type="button" class="vcr-ch-header__menu" id="nt-shell-menu">MENU</button>
      </header>
      <div id="nt-stage" class="nt-stage"></div>
    </div>`;

  const stage = root.querySelector('#nt-stage');
  const titleEl = root.querySelector('#nt-shell-title');
  const header = root.querySelector('#nt-shell-header');

  let currentIdx = 0;
  let active = null;
  let switching = false;
  let headerIdleTimer = null;
  const HEADER_IDLE_MS = 30000;

  // Menu projects need `dir` for legacy chrome compatibility in iframes
  const menuProjects = PROJECTS.map((p) => ({ ...p, dir: p.id }));

  const menu = window.NoiseTableVcrMenu.mount({
    siteName: SITE_NAME,
    projects: menuProjects,
    getCurrentIdx: () => currentIdx,
    onSelect: (idx) => goToIndex(idx),
  });

  const channelInfo = createChannelInfo({
    onClose: () => titleEl.setAttribute('aria-expanded', 'false'),
  });

  function setTitle(project) {
    const label = (project.vcrLabel || project.name).toUpperCase();
    titleEl.textContent = label;
    titleEl.setAttribute('aria-label', `About ${label}`);
    if (project.vcrBg) {
      document.documentElement.style.setProperty('--channel-bg', project.vcrBg);
    }
    document.body.classList.add('nt-channel', 'vcr-channel');
    document.body.dataset.channel = project.id;
  }

  function clearChannelAttr() {
    delete document.body.dataset.channel;
  }

  function hideHeader() {
    header.classList.add('vcr-ch-header--hidden');
    document.body.classList.add('vcr-idle');
  }

  function showHeader() {
    header.classList.remove('vcr-ch-header--hidden');
    document.body.classList.remove('vcr-idle');
  }

  function armHeaderIdle() {
    if (headerIdleTimer) clearTimeout(headerIdleTimer);
    headerIdleTimer = setTimeout(hideHeader, HEADER_IDLE_MS);
  }

  function onHeaderActivity() {
    showHeader();
    armHeaderIdle();
  }

  ['pointermove', 'pointerdown', 'touchstart', 'mousemove'].forEach((ev) => {
    window.addEventListener(ev, onHeaderActivity, { passive: true });
  });
  armHeaderIdle();

  async function mountChannel(project) {
    const shellEl = root.querySelector('#nt-shell');
    const ctx = {
      bus,
      onChannelGo: (idx) => goToIndex(idx),
      onMenuOpen: () => menu.open(),
    };

    if (project.native) {
      shellEl.dataset.host = 'native';
      const mod = await loadNativeChannel(project.id);
      if (!mod?.mount) throw new Error(`Native channel missing mount: ${project.id}`);
      active = { kind: 'native', api: mod, project };
      await mod.mount(stage, ctx);
    } else {
      shellEl.dataset.host = 'legacy';
      const legacy = createLegacyIframeChannel(project);
      active = { kind: 'legacy', api: legacy, project };
      await legacy.mount(stage, ctx);
    }
    assertBusHealthy(`mounted ${project.id}`);
  }

  async function unmountActive() {
    if (!active) return;
    try {
      await active.api.unmount?.();
    } catch (err) {
      console.warn('unmount failed', err);
    }
    stage.innerHTML = '';
    clearChannelAttr();
    active = null;
    assertBusHealthy('after unmount');
  }

  async function goToIndex(idx, { replace = false } = {}) {
    if (switching) return;
    if (idx < 0 || idx >= PROJECTS.length) return;
    if (idx === currentIdx && active) {
      menu.refreshRunning?.();
      return;
    }

    switching = true;
    const project = projectAt(idx);
    try {
      await fadeOutMaster(80);
      await unmountActive();
      currentIdx = idx;
      localStorage.setItem('nt-ch', String(idx));
      setTitle(project);

      const url = `/${project.id}`;
      if (replace) history.replaceState({ idx }, '', url);
      else history.pushState({ idx }, '', url);

      channelInfo.close();
      await mountChannel(project);
      fadeInMaster(160);
      menu.refreshRunning?.();
      window.NoiseTableAnalytics?.channelView?.(project.ch, project.name);
    } catch (err) {
      console.error('channel switch failed', err);
      stage.innerHTML = `<p class="nt-shell-error">Failed to load ${project.id}</p>`;
    } finally {
      switching = false;
    }
  }

  function goDelta(delta) {
    const n = PROJECTS.length;
    goToIndex((currentIdx + delta + n) % n);
  }

  function openChannelInfo() {
    if (menu.isOpen() || channelInfo.isOpen() || switching) return;
    const project = projectAt(currentIdx);
    if (!project) return;
    titleEl.setAttribute('aria-expanded', 'true');
    channelInfo.open(project, titleEl);
  }

  titleEl.addEventListener('click', openChannelInfo);
  header.querySelectorAll('[data-dir]').forEach((btn) => {
    btn.addEventListener('click', () => goDelta(parseInt(btn.dataset.dir, 10)));
  });
  root.querySelector('#nt-shell-menu').addEventListener('click', () => {
    channelInfo.close();
    menu.open();
  });

  document.addEventListener('keydown', (e) => {
    if (channelInfo.isOpen() || menu.isOpen()) return;
    if (e.key === 'm' || e.key === 'M') menu.open();
  });

  window.addEventListener('popstate', () => {
    const parsed = parseChannelFromPath(location.pathname);
    if (parsed) goToIndex(parsed.idx, { replace: true });
  });

  // Resolve initial channel
  const parsed = parseChannelFromPath(location.pathname);
  let startIdx = parsed?.idx;
  if (startIdx == null) {
    const q = new URLSearchParams(location.search);
    if (q.get('ch') != null) {
      const i = parseInt(q.get('ch'), 10);
      if (i >= 0 && i < PROJECTS.length) startIdx = i;
    }
  }
  if (startIdx == null) {
    const stored = parseInt(localStorage.getItem('nt-ch'), 10);
    if (stored >= 0 && stored < PROJECTS.length) startIdx = stored;
  }
  if (startIdx == null) startIdx = indexOfId(HOME_CHANNEL_ID);

  const openMenu = new URLSearchParams(location.search).get('menu') === '1';
  await goToIndex(startIdx, { replace: true });
  if (openMenu) menu.open();

  return { goToIndex, goDelta };
}
